import { cloneDeep, isEqual } from 'lodash';
import moment from 'moment';
import axios, { AxiosError } from 'axios';
import i18n from '@app/i18n';
import { Form, FormAnswer, FormValue, Patient, Question, Submission, Task } from '@app/types';
import { escapeRegExp } from '@utils/escape-regexp';

class QuestionnaireService {
  evaluateQuestionWithToggle = (
    condition: string | boolean,
    values: Record<string, FormValue>,
    groupKey: string,
    optionKey: string | number,
    setFieldValue: (field: string, value: FormValue) => void
  ): boolean => {
    const copiedValues = cloneDeep(values);

    const evaluation = this.evaluateQuestion(condition, copiedValues);
    if (evaluation) {
      if (typeof copiedValues[groupKey] === 'object') {
        copiedValues[groupKey] = [...((copiedValues[groupKey] || []) as string[])].filter((key) => {
          return `${key}` !== `${optionKey}`;
        });
      } else {
        copiedValues[groupKey] =
          copiedValues[groupKey] !== optionKey ? copiedValues[groupKey] : undefined;
      }
    }

    if (!isEqual(copiedValues[groupKey], values[groupKey])) {
      setFieldValue(groupKey, copiedValues[groupKey]);
    }

    return evaluation;
  };

  evaluateQuestion = (condition: string | boolean, values: Record<string, FormValue>): boolean => {
    if (!condition) {
      return false;
    }

    if (typeof condition === 'boolean') {
      return condition;
    }

    const conditions = condition.match(/(\$[a-zA-Z0-9_]+)/g) ?? [];
    if (conditions?.length > 0) {
      for (const c of conditions) {
        const [replaceKey, value] = this.replacePlaceholderWithValue(c, values);
        condition = condition.replace(replaceKey, value);
      }
    }

    if (condition) {
      return condition === '0 == 0';
    }

    // disabled due TrustedScript XSS reported issue
    // try {
    //     // eslint-disable-next-line no-eval
    //     return eval(condition);
    // } catch (_error) {
    //     return false;
    // }
    return false;
  };

  replacePlaceholderWithValue = (
    c: string,
    values: Record<string, FormValue>
  ): [RegExp, string] => {
    let value = values[c.replace('$', '')] as string | undefined;
    if (!value?.length && typeof value !== 'number') {
      value = undefined;
    }

    if (value && Array.isArray(value) && value?.length > 0) {
      value = `["${value.join('","')}"]`;
    } else if (typeof value === 'string') {
      value = `'${value}'`;
    } else if (typeof value === 'undefined') {
      value = "'null'";
    }
    const replaceKey = new RegExp(escapeRegExp(c), 'g');
    return [replaceKey, value];
  };

  /**
   *
   * @param questionKey
   * -1 does not emit check rules back
   * 1 checks for questions.key in group level
   * if evaluation is false will emit back result
   * @param questions group of questions or group options
   * @param values
   * @returns array of available questions
   */
  prepareQuestions = (
    questionKey: string | number,
    questions: Question[],
    values: Record<string, FormValue>
  ): Question[] => {
    return questions?.filter((question) => {
      if (question?.condition?.length || question?.rule?.length) {
        const evaluation = this.evaluateQuestion(
          question.condition || (question.rule as string | boolean),
          values
        );

        if (!evaluation && questionKey === 1) {
          values[question.key] = undefined;
        }

        if (!evaluation && questionKey === 2 && question?.option?.questionKey) {
          values[question.option.questionKey] = undefined;
        }

        if (!evaluation && question.questionKey && typeof questionKey === 'string') {
          values[question.questionKey] = undefined;
        }

        return evaluation;
      }

      return true;
    });
  };

  findQuestion = (questions: Question[], questionKey: string): Question | undefined => {
    return questions.find((q) => q.key === questionKey);
  };

  getAnswerByValue = (
    questions: Question[],
    answers: Record<string, FormValue>,
    questionKey: string
  ): string | string[] => {
    const question = this.findQuestion(questions, questionKey);
    if (question?.options) {
      return (
        question.options
          .filter((option) => {
            const answer = answers[questionKey];
            if (question.type === 'checkbox') {
              return (answer as string[]).indexOf(option.key) > -1;
            }
            return option.key === answer && answer[0];
          })
          ?.map((option) => option?.label) || []
      );
    }

    return '';
  };

  mapQuestions = (
    patient: Patient,
    task: Task,
    key: string,
    answers: Record<string, FormValue>,
    language: string,
    options?: { incomplete?: boolean }
  ): Record<string, string | Form[]> => {
    const [m, jointSide, , questionnaireType] = key.split('_');
    const jointName = patient.joint;
    const questions = i18n.t(`${questionnaireType}:joints.${m}`, {
      returnObjects: true
    }) as Question[];

    const formName = (answers.FORM_NAME as string).split(':');

    const formAnswers: FormAnswer[] = [];
    Object.keys(answers).forEach((questionKey) => {
      const foundQuestion = questions.find((question) => question.key === questionKey);

      // if the question is not found at all, skip
      if (!foundQuestion) {
        return;
      }

      if (foundQuestion?.ignore && this.evaluateQuestion(foundQuestion.ignore, answers)) {
        // prevent mapping ignored questions
        return;
      }

      if (foundQuestion?.key === 'FORM_NAME') {
        // prevent formname as answer
        return;
      }

      if (foundQuestion?.condition && !this.evaluateQuestion(foundQuestion.condition, answers)) {
        // prevent mapping not qualified questions
        return;
      }

      let value = answers[questionKey];
      if (!value || value === 'null') {
        return;
      }

      if (!Array.isArray(value)) {
        value = [`${value}`] as string[];
      }

      formAnswers.push({
        questionname: foundQuestion?.toKey || questionKey,
        values: value
      });
    });

    let iv_location = '' as string | number;
    if (jointName === 'hip') {
      iv_location = 101;
    } else if (jointName === 'knee') {
      iv_location = 201;
    } else if (jointName === 'shoulder') {
      iv_location = 301;
    }

    if (['oss', 'oks', 'ohs'].includes(questionnaireType)) {
      formAnswers.push(
        ...[
          {
            questionname: 'IV_LOCATION_QUESTION',
            values: [jointSide === 'right' ? '1' : '2']
          },
          {
            questionname: 'IV_LOCATION_QUESTION2',
            values: [iv_location.toString()]
          },
          {
            questionname: 'IV_DATE_QUESTION',
            values: [moment().format('DD.MM.YYYY')]
          },
          {
            questionname: 'INTERVAL',
            values: [patient.op === 'post' ? '1' : '0']
          },
          ...(patient.implementation_date
            ? [
                {
                  questionname: 'SURGERY_DATE',
                  values: [`${patient.implementation_date}`]
                }
              ]
            : [])
        ]
      );
    } else if (questionnaireType === 'eq') {
      const interval = patient.op === 'post' ? '1' : '0';

      formAnswers.push(
        ...[
          {
            questionname: 'INTERVAL',
            values: [interval]
          },
          {
            questionname: 'IV_LOCATION_QUESTION',
            values: [jointSide === 'left' ? '2' : '1']
          },
          {
            questionname: 'IV_LOCATION_QUESTION2',
            values: [iv_location.toString()]
          },
          {
            questionname: 'IV_DATE_QUESTION',
            values: [moment().format('DD.MM.YYYY')]
          },
          {
            questionname: 'SURVEY_YN',
            values: ['1']
          },
          ...(patient.implementation_date
            ? [
                {
                  questionname: 'SURGERY_DATE',
                  values: [`${patient.implementation_date}`]
                }
              ]
            : patient.op !== 'post'
              ? [
                  {
                    questionname: 'SURGERY_DATE_UNKNOWN',
                    values: ['1']
                  }
                ]
              : [])
        ]
      );

      if (
        interval === '0' &&
        formAnswers.find((n) => n.questionname === 'EDUCATION_LEVEL')?.values?.[0]?.toString()
      ) {
        formAnswers.push({
          questionname: 'EDUCATION_LEVEL',
          values: [
            formAnswers
              .find((n) => n.questionname === 'EDUCATION_LEVEL')
              ?.values?.[0]?.toString() as string
          ]
        });
      }

      const patientTask = task?.patients?.find((p) => p.id === patient.formTaskPatientId);
      const message = patientTask?.messages?.sort((a, b) =>
        moment(b.sendDate).diff(moment(a.sendDate))
      )?.[0];

      const notificationType = message?.contactType?.toLowerCase();

      if (interval === '0' && notificationType) {
        formAnswers.push({
          questionname: 'RECORDING_TYPE',
          values: [this.mapToRecordingType(notificationType)]
        });
      }
    }

    if (options?.incomplete && ['oss', 'oks', 'ohs'].includes(questionnaireType)) {
      let incompleteKey = 'OSS_INCOMPLETE';
      if (jointName === 'hip') incompleteKey = 'OHS_INCOMPLETE';
      else if (jointName === 'knee') incompleteKey = 'OKS_INCOMPLETE';
      formAnswers.push({
        questionname: incompleteKey,
        values: ['1']
      });
    }

    const form: Form = {
      name: formName[0],
      version: formName[1],
      formcreatedby: patient.formcreatedby,
      formlanguage: language,
      deptID: patient.departmentId,
      answers: formAnswers
    };

    if (patient.formTaskPatientId) {
      form.formTaskPatientId = patient.formTaskPatientId;
    }
    if (patient.taskCaseId) {
      form.taskCaseId = patient.taskCaseId;
    }

    return this.mapPatient(patient, form, language);
  };

  mapToRecordingType = (value: string): string => {
    switch (value) {
      case 'email':
        return '2';
      case 'sms':
        return '3';
      case 'brief':
        return '1';
      case 'tablet':
        return '4';
      default:
        return '99';
    }
  };

  mapPatient = (
    patient: Patient,
    form: Form,
    language: string
  ): Record<string, string | Form[]> => {
    return {
      mrn: patient.mrn,
      dob: patient.dateOfBirth,
      gender: patient.gender === 'FEMALE' ? 'f' : 'm',
      language: language,
      forms: [form]
    };
  };

  processSubmission = async (
    submission: Submission,
    token: string
  ): Promise<[boolean, string[] | null]> => {
    try {
      await axios.post(
        `/patients/${encodeURIComponent(token)}/submit`,
        submission.form.forms[0]
      );
      return [true, null];
    } catch (error) {
      const axiosError = error as AxiosError<{ errors: string[] }>;
      console.error('Failed to submit:', axiosError);
      return [false, axiosError.response?.data?.errors || ['Unknown error occurred']];
    }
  };
}

export default new QuestionnaireService();
